'use client';

import { AspectRatio, Button, Col, Container, Row } from '@geberit/gdds';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useMemo } from 'react';
import key from 'weak-key';

// styles
import styles from './videocenter-channel.module.scss';

// types
import type { VideoTileProps } from './types';

// components
import ChannelMainVideo from './channel-main-video';
import { Translation } from 'components/Translation/Translation';
import VideocenterDropdown from './videocenter-dropdown';
import { InnerHtml } from 'components/InnerHtml/inner-html';
import VideoTile from './video-tile';
import { Headline } from 'components/ContentElementsGdds/headline/headline';
import { MediaOverlay } from 'components/media-overlay';

// utils
import { useChannels } from './useChannels';
import { useTranslationFunction } from 'utils/hooks/use-translations';
import { buildSize, gridSizes } from 'utils/gridSize';
import { decodingContent } from 'utils/decodingContent';
import { useIsMobile } from 'components/App/SizeProvider';
import { Formats } from 'components/ContentElementsGdds/headline/headlines.types';
import { useXy } from 'utils/hooks/use-xy';
import { useNord } from 'utils/hooks/use-nord';

export type VideocenterChannelProps = {
  searchQueryChannelId: string;
  channels: any[];
  page: {
    currentLanguage: string;
  };
};

const skipFirst = (_: unknown, index: number): boolean => index >= 1;

export default function VideocenterChannel({
  searchQueryChannelId = '',
  channels,
  page: { currentLanguage },
}: Readonly<VideocenterChannelProps>) {
  const router = useRouter();
  const pathname = usePathname();
  const translate = useTranslationFunction();
  const { channel, firstVideo, loadable, sharedVideo, videos, handleLoadMore } = useChannels({
    currentLanguage,
    searchQueryChannelId,
    channels,
  });
  const isMobile = useIsMobile();
  const params = useSearchParams();
  const videoId = String(params.get('video'));
  const isXy = useXy();
  const isNord = useNord();
  const isNordOrXy = isXy || isNord;

  const currentVideoIndex = useMemo(
    () => (videoId && videos?.length ? videos.findIndex((v) => v.video.videoId === videoId) : -1),
    [videoId, videos],
  );

  const overlayItems = useMemo(
    () =>
      videos?.map((video) => ({
        pictureObject: {} as ImageObject,
        pictureObjectMobile: {} as ImageObjectMobile,
        pictureAlt: video.title,
        picture: '',
        overlay: {
          text: video.description,
          link: null,
        },
        pictureCaption: video.title,
        video: video.video,
        title: video.title,
        description: video.description,
      })) || [],
    [videos],
  );

  const videosExceptFirst = useMemo(() => videos?.filter(skipFirst) || [], [videos]);

  const handleOverlayClose = useCallback(() => {
    const { video: _, ...restQuery } = Object.fromEntries(params);
    const updatedQuery = new URLSearchParams(restQuery);
    router.replace(`${pathname}?${updatedQuery.toString()}`, { scroll: false });
  }, [params, pathname, router]);

  const getVideoOnclickHandler = useCallback(
    ({ video: { videoId } }: VideoTileProps) =>
      () => {
        const writeQuery = new URLSearchParams(params.toString());
        writeQuery.set('video', videoId);
        router.replace(`${pathname}?${writeQuery.toString()}`, { scroll: false });
      },
    [params, pathname, router],
  );

  return (
    <>
      <Container
        maxContentWidth={buildSize(gridSizes.gddsFullGrid)}
        className={styles.videoChannelWrapper}
      >
        <Row className={styles.titleRow}>
          <Col size={[4, 8, 12]}>
            <Headline
              tag={Formats.h1}
              title={translate('web20_videocenter_channel')}
              subtitle={channel ? channel.title : ''}
            />
          </Col>
        </Row>
        <Row className={styles.mainVideoRow}>
          <Col size={[4, 5, 7]} className={styles.leftCol}>
            {firstVideo && (
              <AspectRatio ratio={'16:9'}>
                <ChannelMainVideo
                  video={firstVideo.video}
                  title={decodingContent(firstVideo.title)}
                  key={key(firstVideo.video)}
                />
              </AspectRatio>
            )}
          </Col>
          <Col size={[4, 3, 5]} className={styles.rightCol}>
            {firstVideo?.title && (
              <Headline
                tag={isNordOrXy ? Formats.h2 : Formats.h3}
                variant={Formats.h3}
                title={decodingContent(firstVideo.title)}
              />
            )}
            {firstVideo?.description && (
              <InnerHtml
                className={styles.copyText}
                as="div"
                content={decodingContent(firstVideo.description)}
                isGdds
              />
            )}
          </Col>
        </Row>
        <Row className={styles.dropdownRow}>
          <Col size={[4, 4, 3]}>
            <VideocenterDropdown searchQueryChannelId={searchQueryChannelId} channels={channels} />
          </Col>
          <Col size={[0, 4, 9]}></Col>
        </Row>
        <Row>
          <Col>
            <div className={styles.videocenterChannel}>
              {videosExceptFirst.map((videoProps: VideoTileProps) => (
                <VideoTile
                  key={key(videoProps.video)}
                  onClick={getVideoOnclickHandler(videoProps)}
                  video={videoProps}
                  screenSize="small"
                  open={sharedVideo === videoProps.title}
                />
              ))}
            </div>
          </Col>
          <Col>
            {loadable && (
              <div className={styles.loadmoreButton} key="button-item-wrapper">
                <Button key="button" symbol="refresh" onClick={handleLoadMore} stretched={isMobile}>
                  <Translation id="web20_more_results" key="translation" />
                </Button>
              </div>
            )}
          </Col>
        </Row>
      </Container>

      {videoId && currentVideoIndex >= 0 && overlayItems.length > 0 && (
        <MediaOverlay items={overlayItems} index={currentVideoIndex} onClose={handleOverlayClose} />
      )}
    </>
  );
}
